import { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FetchingState } from "@multiweb/types/react";
import { IParticipant } from "../../../../types";
import { UiModelCreator } from "../../../../UiModelCreator";
import { getAvailableParticipants, loadParticipants } from "./utils";

type ParticipantsProviderComponentChildrenProps = {
    readonly items: readonly IParticipant[];
    readonly canLoadNextItems: boolean;
    readonly fetchingState: FetchingState;
    readonly loadNextItems: (count: number) => void;
};

export type ParticipantsProviderComponentProps = {
    readonly count: number;
    readonly definitionParticipants: readonly IParticipant[];
    readonly selectedParticipants: readonly IParticipant[];
    readonly keyword: string;
    readonly children: (props: ParticipantsProviderComponentChildrenProps) => ReactElement;
};

const DEBOUNCE_DURATION_MS = 300;

const DEFAULT_PARTICIPANTS_ARRAY: readonly IParticipant[] = [
    UiModelCreator.createSystemParticipant(),
    UiModelCreator.createOwnerParticipant(),
];

export const ParticipantsProviderComponent: FunctionComponent<ParticipantsProviderComponentProps> = ({ definitionParticipants, selectedParticipants, keyword, count, children }) => {
    const isMounted = useRef<boolean | null>(null);
    const isFirstRender = useRef<boolean>(true);
    const [canLoadNextItems, setHasNextItems] = useState<boolean>(true);
    const [fetchingState, setFetchingState] = useState<FetchingState>(FetchingState.success);
    const [availableParticipants, setAvailableParticipants] = useState<readonly IParticipant[]>([...DEFAULT_PARTICIPANTS_ARRAY, ...definitionParticipants]);
    // Zabraňuje to probliknutí seznamu hráčů, protože se stihnou vyfiltrovat definiční hráči před načtením dat z DB.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filteredParticipants = useMemo<readonly IParticipant[]>(() => getAvailableParticipants(availableParticipants, selectedParticipants, keyword), [selectedParticipants, availableParticipants]);

    const handleLoadParticipants = useCallback(async (startIndex: number, count: number, keyword: string, reload: boolean = false) => {
        setFetchingState(FetchingState.request);
        const [newParticipants, hasMore] = await loadParticipants(startIndex, count, keyword);

        if (isMounted.current === false)
            return;

        setAvailableParticipants(reload ?
            [...DEFAULT_PARTICIPANTS_ARRAY, ...definitionParticipants, ...newParticipants]
            : (previousParticipants) => [...previousParticipants, ...newParticipants]);
        setHasNextItems(hasMore);
        setFetchingState(FetchingState.success);
    }, [definitionParticipants]);

    const loadNextItems = useCallback((count: number) => {
        const startIndex = availableParticipants.length - DEFAULT_PARTICIPANTS_ARRAY.length - definitionParticipants.length;
        handleLoadParticipants(startIndex, count, keyword);
    }, [availableParticipants, definitionParticipants, keyword, handleLoadParticipants]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        let canceled = false;

        setTimeout(() => {
            if (!canceled && !isFirstRender.current)
                handleLoadParticipants(0, count, keyword, true);
        }, DEBOUNCE_DURATION_MS);

        return () => {
            canceled = true;
            isFirstRender.current = false;
        };
    }, [count, keyword, handleLoadParticipants]);

    return children({ items: filteredParticipants, loadNextItems, fetchingState, canLoadNextItems });
};
